import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { findSeriesBasedOnKey } from '@/features/_shared/chart/lib/findSeriesBasedOnKey';
import { getShortTitle } from '@/features/_shared/chart/lib/getShortTitle';
import { isTemplateValid } from '@/features/_shared/chart/lib/isTemplateValid';
import { parseDataToChartSeries } from '@/features/_shared/chart/lib/parseDataToChartSeries';
import { takeForwardCurveData } from '@/features/_shared/chart/lib/takeForwardCurveData';
import {
  INVALID_PERIODS_ERROR,
  NO_DATA_ERROR,
  validateCurveData
} from '@/features/_shared/chart/lib/validateCurveData';
import { useUserConfig } from '@/features/_shared/user';
import { chartOptions } from '@/features/charts/api/charts.options';
import type { TemplateChartSeries } from '@/shared/types/charts';

const useForwardCurveChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  setComparisonTemplateSeries?: Dispatch<SetStateAction<TemplateChartSeries[]>>,
  isInViewport = true
) => {
  const { data: userConfigData } = useUserConfig();
  const timezone = userConfigData?.timezone.value ?? 'UTC';
  // Fetch chart data using React Query
  const {
    data: chartData,
    isError: isChartDataError,
    isLoading: isChartDataLoading
  } = useQuery({
    ...chartOptions.data('forwardSimple', templateChartSeries, timezone),
    enabled: templateChartSeries !== undefined && isInViewport
  });

  // Validate symbols and determine error type
  const validationResult = useMemo(() => {
    if (!chartData?.symbols) {
      return { invalidSymbols: [], errorType: null };
    }
    return validateCurveData(chartData.symbols);
  }, [chartData]);

  const { invalidSymbols, errorType } = validationResult;

  const validSymbols = useMemo(() => {
    if (!chartData?.symbols) return [];
    return chartData.symbols.filter(symbol => !invalidSymbols.includes(symbol));
  }, [chartData, invalidSymbols]);

  // Track previous error to prevent duplicate snackbars
  const previousErrorTypeRef = useRef<typeof errorType>(null);

  // Show error notification only once when error type changes
  useEffect(() => {
    if (errorType && errorType !== previousErrorTypeRef.current) {
      const message = errorType === 'no-data' ? NO_DATA_ERROR : INVALID_PERIODS_ERROR;
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 5000,
        preventDuplicate: true
      });
      previousErrorTypeRef.current = errorType;
    } else if (!errorType) {
      previousErrorTypeRef.current = null;
    }
  }, [errorType]);

  // Update comparison series when invalid symbols are found
  useEffect(() => {
    if (!setComparisonTemplateSeries || invalidSymbols.length === 0) return;

    setComparisonTemplateSeries(prev =>
      prev.filter(templateSeries => isTemplateValid(templateSeries, invalidSymbols))
    );
  }, [invalidSymbols, setComparisonTemplateSeries]);

  // Transform valid symbols into chart series
  const chartSeries = useMemo(() => {
    if (validSymbols.length === 0) return undefined;

    return validSymbols.flatMap(({ key, metadata, series, transactionDate }, symbolIndex) => {
      const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
      const shortTitle = getShortTitle(templateSeries);
      const seriesColor = templateSeries?.settings.color;
      const parseableElements = {
        symbolIndex,
        shortTitle,
        key,
        metadata,
        transactionDate,
        color: seriesColor
      };
      return parseDataToChartSeries(series, parseableElements, false, takeForwardCurveData);
    });
  }, [validSymbols, templateChartSeries]);

  return {
    loading: isChartDataLoading || !templateChartSeries || (!isInViewport && !chartData),
    error: isChartDataError,
    chartSeries
  };
};

export default useForwardCurveChartSeries;
